import { useState } from "react";
import { Form } from "react-final-form";
import { Trans, useTranslation } from "react-i18next";
import i18next from "src/i18n/config";
import { recoverPassword } from "src/ui/containers/account/api";
import { AuthFlowContainer } from "src/ui/shared/AuthFlowContainer";

import { Box, InputAdornment, Typography } from "@mui/material";
import { withMetaDescription } from "@navigation/utils/withMetaDescription";
import ApmLink from "@shared/ApmLink";
import { PrimaryButton } from "@shared/CustomButton";
import { TextField } from "@shared/form/TextField";
import { showErrorToast, showSuccessToast } from "@shared/toasts/Toasts";

import { emailRegex } from "../../../unauthorized/components/LoginPage/LoginPage";

import { useStyles } from "./PasswordRecoveryPageStyles";
import { PasswordRecoverySuccess } from "./PasswordRecoverySuccess";

interface FormValues {
    email: string;
}

const PasswordRecoveryPage = () => {
    const { classes } = useStyles();
    const { t } = useTranslation(["pages"]);
    const [recoveryEmail, setRecoveryEmail] = useState<string | null>(null);

    const validate = (values: FormValues) => {
        const errors: Partial<FormValues> = {};

        if (!values.email) {
            errors.email = t("pages:passwordRecoveryPage.validation.emailRequired");
        } else if (!emailRegex.test(values.email)) {
            errors.email = t("pages:passwordRecoveryPage.validation.invalidEmail");
        }

        return errors;
    };

    const onSubmit = async (values: FormValues) => {
        try {
            const { message } = await recoverPassword(values);
            showSuccessToast(message);
            setRecoveryEmail(values.email);
        } catch (err) {
            const error = err as Error;
            showErrorToast(error.message);
        }
    };

    const loginLink = (
        <Typography className={classes.footerText}>
            <Trans
                ns="pages"
                i18nKey="passwordRecoveryPage.wantToLoginInsteadWithLink"
                components={{
                    Link: <ApmLink role="link" parent="Password recovery page" title="Go back" to="/login" />,
                }}
            />
        </Typography>
    );

    if (recoveryEmail) {
        return (
            <AuthFlowContainer>
                <PasswordRecoverySuccess email={recoveryEmail} />
                {loginLink}
            </AuthFlowContainer>
        );
    }

    return (
        <AuthFlowContainer>
            <Box className={classes.titleContainer}>
                <Typography variant="h5">{t("pages:passwordRecoveryPage.title")}</Typography>
            </Box>
            <Form
                onSubmit={onSubmit}
                validate={validate}
                validateOnBlur={false}
                render={({ handleSubmit, submitting }) => (
                    <form onSubmit={handleSubmit}>
                        <div className={classes.formContainer}>
                            <TextField
                                placeholder={t("pages:passwordRecoveryPage.emailPlaceholder")}
                                name="email"
                                type="text"
                                fullWidth
                                showErrorOnSubmit
                                slotProps={{
                                    input: {
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <i className="fa-solid fa-fw fa-envelope" />
                                            </InputAdornment>
                                        ),
                                    },
                                }}
                            />
                            <PrimaryButton type="submit" disabled={submitting} loading={submitting} fullWidth>
                                {t("pages:passwordRecoveryPage.submitButton")}
                            </PrimaryButton>
                            {loginLink}
                        </div>
                    </form>
                )}
            />
            <Typography className={classes.infoText}>{t("pages:passwordRecoveryPage.infoText")}</Typography>
        </AuthFlowContainer>
    );
};

export default withMetaDescription(PasswordRecoveryPage, {
    title: i18next.t("pages:passwordRecoveryPage.metaTitle"),
});
